import React from 'react';
import { ArrowRightIcon, CheckIcon, MinusIcon } from 'lucide-react';
import { LandingHeader } from '../components/LandingHeader';
import { MobileComparison } from '../components/pricing/MobileComparison';
import { comparisonRows } from '../data/pricingComparison';
import { formatPrice, pricingPlans } from '../data/pricingPlans';
function planHref(planId: string) {
  return planId === 'caas' ? 'mailto:help@creditmanager.uk.com?subject=Credit%20Control%20as%20a%20Service' : `/signup?plan=${planId}`;
}
function planCta(planId: string) {
  return planId === 'caas' ? 'Enquire' : 'Start free trial';
}
function ComparisonValue({
  value
}: {
  value: string | boolean;
}) {
  if (value === true) {
    return <>
        <CheckIcon size={17} className="mx-auto text-[#5B5299]" aria-hidden="true" data-id="element-850" data-mp="stc6tle5dsz2" />
        <span className="sr-only" data-id="element-851" data-mp="1giwpe36e2o7t">Included</span>
      </>;
  }
  if (value === false) {
    return <>
        <MinusIcon size={16} className="mx-auto text-stone-300" aria-hidden="true" data-id="element-852" data-mp="1lcsoxa2awj53" />
        <span className="sr-only" data-id="element-853" data-mp="1e14ie56vbgkm">Not included</span>
      </>;
  }
  return <span className="text-sm font-semibold text-[#041F60]" data-id="element-854" data-mp="1dz1nb6quyx91">{value}</span>;
}
export function PricingPage() {
  return <div id="top" className="min-h-screen w-full bg-white text-[#041F60]" data-id="element-855" data-mp="1nzbyymx8vwm3">
      <a href="#main-content" className="sr-only fixed left-4 top-4 z-50 rounded-lg bg-[#041F60] px-4 py-2 text-sm font-bold text-white focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-856" data-mp="1nx7m3fhdbn9o">
        Skip to content
      </a>
      <LandingHeader data-id="element-857" data-mp="2bnbmldz9f6wg" />

      <main id="main-content" data-id="element-858" data-mp="2bmk494uob5o4">
        <section className="border-b border-[#A6B6F8]/50 bg-[#A6B6F8]/20" data-id="element-859" data-mp="26ptgdvcmn5s5">
          <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20" data-id="element-860" data-mp="26npcvein48se">
            <div className="mx-auto max-w-2xl text-center" data-id="element-861" data-mp="hb5b0e2geo3m">
              <h1 className="text-4xl font-bold tracking-[-0.045em] sm:text-5xl sm:leading-[1.06]" data-id="element-862" data-mp="ccal6s62jza0">
                Pricing.
              </h1>
              <p className="mt-5 text-base leading-7 text-[#3A3A3A] sm:text-lg" data-id="element-863" data-mp="121hc5v94y461">
                Our virtual credit packages start from £45 a month. Every plan
                includes a free trial from day one and runs for a 12-month
                contract period, billed monthly.
              </p>
            </div>

            <ul className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-id="element-864" data-mp="1fx30s1bgilxx">
              {pricingPlans.map(plan => <li key={plan.id} className="h-full" data-id="element-865" data-mp="1ozlyy84asadu">
                  <article className={`flex h-full flex-col rounded-2xl bg-white p-6 sm:p-7 ${plan.recommended ? 'border-2 border-[#041F60] shadow-[0_14px_40px_rgba(4,31,96,0.14)]' : 'border border-[#A6B6F8]/60'}`} data-id="element-866" data-mp="1oyayoia29krh">
                    <div className="flex min-h-6 items-start justify-between gap-2" data-id="element-867" data-mp="28nlc1pmc3tmf">
                      <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#5B5299]" data-id="element-868" data-mp="3t5vuth3x3hnl">
                        {plan.name}
                      </h2>
                      {plan.recommended && <span className="shrink-0 rounded-full bg-[#4BE4FF] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#041F60]" data-id="element-869" data-mp="3j7g0745olq9c">
                          Most popular
                        </span>}
                    </div>

                    <p className="mt-4 flex items-baseline gap-2" data-id="element-870" data-mp="as63kbkk9d8y">
                      <span className="text-4xl font-bold tracking-[-0.04em] text-[#041F60]" data-id="element-871" data-mp="aso61pxtgnpb">
                        {plan.priceLabel || formatPrice(plan.monthlyPrice)}
                      </span>
                      <span className="text-xs font-semibold text-stone-500" data-id="element-872" data-mp="11tj0jcqxk2t">
                        / month
                      </span>
                    </p>
                    <p className="mt-3 text-sm leading-6 text-[#3A3A3A]" data-id="element-873" data-mp="tqs4nqg2v4cv">
                      {plan.tagline}
                    </p>

                    <ul className="mt-5 space-y-2.5 border-t border-[#A6B6F8]/50 pt-5" data-id="element-874" data-mp="e7fte5fs0gu3">
                      {plan.features.map(feature => <li key={feature} className="flex gap-2.5 text-sm leading-6 text-[#3A3A3A]" data-id="element-875" data-mp="4b2y534l1ux8">
                          <CheckIcon size={15} className="mt-1 shrink-0 text-[#5B5299]" aria-hidden="true" data-id="element-876" data-mp="9bqxcc2yti2j" />
                          {feature}
                        </li>)}
                    </ul>

                    <a href={planHref(plan.id)} className={`mt-auto flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${plan.recommended ? 'bg-[#041F60] text-white hover:bg-[#031849] focus:ring-[#041F60]' : 'border border-[#041F60] text-[#041F60] hover:bg-[#041F60] hover:text-white focus:ring-[#041F60]'}`} style={{
                  marginTop: '2rem'
                }} data-id="element-877" data-mp="s809gtub3yjw">
                      {planCta(plan.id)}
                      <ArrowRightIcon size={16} aria-hidden="true" data-id="element-878" data-mp="3a8klhvruxro3" />
                    </a>
                  </article>
                </li>)}
            </ul>

          </div>
        </section>

        <section className="bg-white" data-id="element-879" data-mp="2hjvu4oglt0pj">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20" data-id="element-880" data-mp="2vhkcnpn77vrf">
            <div className="mx-auto max-w-2xl text-center" data-id="element-881" data-mp="2rfyuborwap9u">
              <h2 className="text-3xl font-bold tracking-[-0.035em] sm:text-4xl" data-id="element-882" data-mp="2refe1y68oe82">
                Compare the plans.
              </h2>
              <p className="mt-4 text-base leading-7 text-[#3A3A3A]" data-id="element-883" data-mp="6prmqp3qn7dz">
                Every plan includes the debtor dashboard, unlimited users and
                unlimited credit alerts. The differences are in chasing and
                credit report volume.
              </p>
            </div>

            <MobileComparison planHref={planHref} planCta={planCta} data-id="element-884" data-mp="39ifdv2lv5vrk" />

            <div className="mt-10 hidden overflow-x-auto lg:block" data-id="element-885" data-mp="39g1kqzaqutmn">
              <table className="w-full min-w-[820px] border-collapse text-left" data-id="element-886" data-mp="3dig55nu2a1g1">
                <caption className="sr-only" data-id="element-887" data-mp="2gu9v2ledegud">
                  Feature comparison across the Basic, Advanced, Premium and
                  Credit Control as a Service plans.
                </caption>
                <thead data-id="element-888" data-mp="2ptwyfcqntwq2">
                  <tr className="border-b-2 border-[#041F60]" data-id="element-889" data-mp="2pslu9btaymyf">
                    <th scope="col" className="w-[34%] py-4 pr-4 align-bottom text-xs font-bold uppercase tracking-[0.14em] text-[#5B5299]" data-id="element-890" data-mp="2ul55i24ip602">
                      Feature
                    </th>
                    {pricingPlans.map(plan => <th key={plan.id} scope="col" className="px-3 py-4 align-bottom text-center" data-id="element-891" data-mp="1uk0dmo914q9p">
                        <span className="block text-xs font-bold uppercase tracking-[0.08em] text-[#5B5299]" data-id="element-892" data-mp="1ljbwvls94bt4">
                          {plan.name}
                        </span>
                        <span className="mt-1.5 block text-lg font-bold tracking-tight text-[#041F60]" data-id="element-893" data-mp="1bmqr2y8eqwo8">
                          {plan.priceLabel || formatPrice(plan.monthlyPrice)}
                          <span className="text-xs font-semibold text-stone-500" data-id="element-894" data-mp="11wdyciqfvx70">
                            {' '}
                            / mo
                          </span>
                        </span>
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-895" data-mp="nwwfejztre5k">
                  {comparisonRows.map((row, index) => <tr key={row.feature} className="border-b border-[#A6B6F8]/40" style={{
                  backgroundColor: index % 2 === 1 ? 'rgba(166,182,248,0.08)' : 'transparent'
                }} data-id="element-896" data-mp="nyz4n1t5l398" data-mpi={index}>
                      <th scope="row" className="py-3.5 pr-4 text-sm font-semibold text-[#041F60]" data-id="element-897" data-mp="27ttsg68qlxk8">
                        {row.feature}
                      </th>
                      {row.values.map((value, valueIndex) => <td key={pricingPlans[valueIndex].id} className="px-3 py-3.5 text-center" data-id="element-898" data-mp="1k01yt9pbbtj9" data-mpi={valueIndex}>
                          <ComparisonValue value={value} data-id="element-899" data-mp="1a4gzjjbmp7d7" />
                        </td>)}
                    </tr>)}
                  <tr data-id="element-900" data-mp="vgqegj2qipau">
                    <td className="py-6 pr-4" data-id="element-901" data-mp="vib908p1u88p" />
                    {pricingPlans.map(plan => <td key={plan.id} className="px-3 py-6 text-center" data-id="element-902" data-mp="qj8f9keirog1">
                        <a href={planHref(plan.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#041F60] px-3.5 py-2.5 text-xs font-bold text-[#041F60] transition hover:bg-[#041F60] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-2" data-id="element-903" data-mp="15fz6v7hybvv3">
                          {planCta(plan.id)}
                        </a>
                      </td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="navy-texture border-t border-[#031849] text-white" data-id="element-904" data-mp="1x5u3zqc1wpub">
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-5 py-14 sm:px-8 sm:py-20 md:flex-row md:items-center" data-id="element-905" data-mp="1t5k2apvakll9">
            <div className="max-w-xl" data-id="element-906" data-mp="171o8nimgbihf">
              <h2 className="text-3xl font-bold tracking-[-0.035em] sm:text-4xl" data-id="element-907" data-mp="129wr1xl9wn3f">
                Get started now.
              </h2>
              <p className="mt-3 text-base leading-7 text-[#A6B6F8]" data-id="element-908" data-mp="xblf9hbf42ik">
                Start your free trial and bring credit, collections and invoice
                finance into one practical view.
              </p>
            </div>
            <a href="/signup" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#4BE4FF] px-5 py-3.5 text-sm font-bold text-[#041F60] transition hover:bg-[#38d9f2] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#041F60]" data-id="element-909" data-mp="1px4vyeeshu4x">
              Start your free trial{' '}
              <ArrowRightIcon size={17} aria-hidden="true" data-id="element-910" data-mp="4mxpb8fq4oyo" />
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white" data-id="element-911" data-mp="iq43l8h5r57">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-912" data-mp="2c7mukfnadc6l">
          <p className="text-sm font-semibold text-[#041F60]" data-id="element-913" data-mp="2g8gcmse1rwrk">Credit Manager</p>
          <a href="mailto:help@creditmanager.uk.com" className="text-sm font-semibold text-[#5B5299] underline decoration-[#A6B6F8] underline-offset-4 transition hover:text-[#041F60] focus:text-[#041F60] focus:outline-none" data-id="element-914" data-mp="2l8kuomulszjw">
            help@creditmanager.uk.com
          </a>
          <p className="text-xs text-stone-500" data-id="element-915" data-mp="2zwfsghngxu3u">© 2026 Credit Manager</p>
        </div>
      </footer>
    </div>;
}